import React, { useState, useEffect } from "react";
import { Menu, Drawer, Button } from "antd";
import {
  RiDashboardFill,
  RiNotification3Line,
  RiBookOpenLine,
  RiFileList3Line,
  RiAwardLine,
  RiShoppingCartLine,
  RiUserLine,
  RiSettings3Line,
  RiMoreLine,
} from "react-icons/ri";
import { AiFillProject } from "react-icons/ai";
import { useNavigate, useLocation } from "react-router-dom";

const StudentSidebar = ({ isOpen, activePath, onPathChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Theme detection - ONLY from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const shouldBeDark = savedTheme === "dark";
    setIsDarkMode(shouldBeDark);
  }, []);

  // Listen for theme changes from ThemeSwitcher
  useEffect(() => {
    const handleThemeChange = () => {
      const savedTheme = localStorage.getItem("theme");
      const shouldBeDark = savedTheme === "dark";
      setIsDarkMode(shouldBeDark);
    };

    window.addEventListener("themeChanged", handleThemeChange);
    return () => window.removeEventListener("themeChanged", handleThemeChange);
  }, []);

  // Detect mobile width
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize(); // check on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleClick = (e) => {
    setDrawerVisible(false);
    onPathChange(`/student-panel/${e.key}`);
    navigate(`/student-panel/${e.key}`);
  };

  // Get current active key from path
  const getActiveKey = () => {
    const path = location.pathname;
    if (path.includes("student-dashboard")) return "student-dashboard";
    if (path.includes("announcements")) return "announcements";
    if (
      path.includes("courses") &&
      !path.includes("saved-courses") &&
      !path.includes("course-curriculum")
    )
      return "courses";
    if (path.includes("saved-courses")) return "saved-courses";
    if (path.includes("exams")) return "exams";
    if (path.includes("certificates")) return "certificates";
    if (path.includes("projects")) return "projects";
    if (path.includes("purchases")) return "purchases";
    if (path.includes("profile")) return "profile";
    if (path.includes("settings")) return "settings";
    return "student-dashboard";
  };

  // Colors based on theme
  const backgroundColor = isDarkMode ? "#111827" : "#ffffff";
  const borderColor = isDarkMode ? "#374151" : "#e5e7eb";
  const textColor = isDarkMode ? "#ffffff" : "#1f2937";
  const activeColor = "#3b82f6";
  const iconColor = isDarkMode ? "#9ca3af" : "#6b7280";
  const activeIconColor = "#3b82f6";
  const hoverBackgroundColor = isDarkMode ? "#1f2937" : "#f3f4f6";

  const menuItemStyle = {
    backgroundColor: "transparent",
    color: textColor,
    margin: "2px 8px",
    borderRadius: "6px",
  };

  const activeMenuItemStyle = {
    backgroundColor: isDarkMode ? "#1f2937" : "#e0f2fe",
    color: activeColor,
    margin: "2px 8px",
    borderRadius: "6px",
  };

  const bottomMenuItems = [
    {
      key: "student-dashboard",
      icon: (
        <RiDashboardFill
          size={20}
          color={
            getActiveKey() === "student-dashboard" ? activeIconColor : iconColor
          }
        />
      ),
      label: "Dashboard",
    },
    {
      key: "courses",
      icon: (
        <RiBookOpenLine
          size={20}
          color={getActiveKey() === "courses" ? activeIconColor : iconColor}
        />
      ),
      label: "Courses",
    },
    {
      key: "exams",
      icon: (
        <RiFileList3Line
          size={20}
          color={getActiveKey() === "exams" ? activeIconColor : iconColor}
        />
      ),
      label: "Exams",
    },
    {
      key: "certificates",
      icon: (
        <RiAwardLine
          size={20}
          color={getActiveKey() === "certificates" ? activeIconColor : iconColor}
        />
      ),
      label: "Certificates",
    },
    {
      key: "more",
      icon: (
        <RiMoreLine
          size={20}
          color={getActiveKey() === "more" ? activeIconColor : iconColor}
        />
      ),
      label: "More",
    },
  ];

  const moreMenuItems = [
    { key: "announcements", icon: <RiNotification3Line size={18} />, label: "Announcements" },
    { key: "saved-courses", icon: <RiBookOpenLine size={18} />, label: "Saved Courses" },
    { key: "projects", icon: <AiFillProject size={18} />, label: "Projects" },
    { key: "purchases", icon: <RiShoppingCartLine size={18} />, label: "Purchases" },
    { key: "profile", icon: <RiUserLine size={18} />, label: "Profile" },
    { key: "settings", icon: <RiSettings3Line size={18} />, label: "Settings" },
  ];

  // Mobile Bottom Navigation
  if (isMobile) {
    return (
      <>
        {/* Bottom Navigation Bar */}
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: "60px",
            backgroundColor: backgroundColor,
            borderTop: `1px solid ${borderColor}`,
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            zIndex: 1000,
            padding: "0 8px",
            backdropFilter: "blur(10px)",
          }}
        >
          {bottomMenuItems.map(({ key, icon, label }) => (
            <button
              key={key}
              onClick={() => {
                if (key === "more") {
                  setDrawerVisible(true);
                } else {
                  handleClick({ key });
                }
              }}
              style={{
                border: "none",
                background: getActiveKey() === key ? 
                  (isDarkMode ? "#1f2937" : "#e0f2fe") : "transparent",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontSize: 11,
                color: getActiveKey() === key ? activeColor : textColor,
                cursor: "pointer",
                fontWeight: getActiveKey() === key ? "600" : "400",
                padding: "8px 12px",
                borderRadius: "8px",
                minWidth: "60px",
                transition: "all 0.2s ease",
                flex: 1,
                margin: "0 2px",
              }}
            >
              {icon}
              <span style={{ marginTop: "2px" }}>{label}</span>
            </button>
          ))}
        </div>

        {/* More Options Drawer */}
        <Drawer
          title={
            <span style={{ color: textColor, fontWeight: 600 }}>
              More Options
            </span>
          }
          placement="bottom"
          closable={true}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          height="auto"
          style={{
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
          bodyStyle={{
            padding: 0,
            backgroundColor: backgroundColor,
          }}
          headerStyle={{
            backgroundColor: backgroundColor,
            borderBottom: `1px solid ${borderColor}`,
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
          closeIcon={<span style={{ color: textColor, fontSize: "18px" }}>×</span>}
        >
          <Menu
            mode="vertical"
            onClick={handleClick}
            selectedKeys={[getActiveKey()]}
            style={{
              borderRight: 0,
              backgroundColor: backgroundColor,
              padding: "8px",
            }}
          >
            {moreMenuItems.map(({ key, icon, label }) => (
              <Menu.Item
                key={key}
                icon={React.cloneElement(icon, {
                  color:
                    getActiveKey() === key ? activeIconColor : iconColor,
                })}
                style={
                  getActiveKey() === key ? activeMenuItemStyle : menuItemStyle
                }
              >
                <span
                  style={{
                    color:
                      getActiveKey() === key ? activeColor : textColor,
                    fontWeight: getActiveKey() === key ? "600" : "400",
                  }}
                >
                  {label}
                </span>
              </Menu.Item>
            ))}
          </Menu>
        </Drawer>
      </>
    );
  }
// Desktop Sidebar - Collapsible (Icons remain visible when closed)
return (
  <div
    style={{
      width: isOpen ? "240px" : "64px",
      transition: "all 0.3s ease",
      minHeight: "calc(100vh - 64px)",
      overflowX: "hidden",
      backgroundColor: backgroundColor,
      borderRight: `1px solid ${borderColor}`,
      position: "relative",
      boxShadow: isOpen
        ? isDarkMode
          ? "2px 0 8px rgba(0,0,0,0.5)"
          : "2px 0 8px rgba(0,0,0,0.1)"
        : "none",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <div style={{ 
      padding: "16px 0",
      width: "100%",
    }}>
      <Menu
        mode="vertical"
        selectedKeys={[getActiveKey()]}
        onClick={handleClick}
        style={{
          borderRight: 0,
          backgroundColor: "transparent",
          width: "100%",
        }}
      >
        {[
          { key: "student-dashboard", icon: <RiDashboardFill size={20} />, label: "Dashboard" },
          { key: "announcements", icon: <RiNotification3Line size={20} />, label: "Announcements" },
          { key: "courses", icon: <RiBookOpenLine size={20} />, label: "My Courses" },
          { key: "saved-courses", icon: <RiBookOpenLine size={20} />, label: "Saved Courses" },
          { key: "exams", icon: <RiFileList3Line size={20} />, label: "Exams" },
          { key: "certificates", icon: <RiAwardLine size={20} />, label: "Certificates" },
          { key: "projects", icon: <AiFillProject size={20} />, label: "Projects" },
          { key: "purchases", icon: <RiShoppingCartLine size={20} />, label: "Purchases" },
          { key: "profile", icon: <RiUserLine size={20} />, label: "Profile" },
          { key: "settings", icon: <RiSettings3Line size={20} />, label: "Settings" },
        ].map(({ key, icon, label }) => (
          <Menu.Item
            key={key}
            icon={React.cloneElement(icon, {
              color: getActiveKey() === key ? activeIconColor : iconColor,
            })}
            style={{
              ...(getActiveKey() === key ? activeMenuItemStyle : menuItemStyle),
              display: "flex",
              alignItems: "center",
              height: "40px",
              padding: isOpen ? "0 16px" : "0 8px",
              justifyContent: isOpen ? "flex-start" : "center",
              margin: "2px 8px",
            }}
          >
            {isOpen ? (
              <span
                style={{
                  color: getActiveKey() === key ? activeColor : textColor,
                  fontWeight: getActiveKey() === key ? "600" : "400",
                  marginLeft: "12px",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
            ) : (
              // Tooltip or minimal label for collapsed state
              <div
                style={{
                  position: "absolute",
                  left: "100%",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                  color: textColor,
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: `1px solid ${borderColor}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  whiteSpace: "nowrap",
                  fontSize: "12px",
                  fontWeight: "500",
                  opacity: 0,
                  pointerEvents: "none",
                  transition: "opacity 0.2s ease",
                  zIndex: 1000,
                }}
                className="sidebar-tooltip"
              >
                {label}
              </div>
            )}
          </Menu.Item>
        ))}
      </Menu>
    </div>
  </div>
);
};

export default StudentSidebar;